/**
 * S-52 layer set, driven by ChartFeatureProps.
 *
 * Each entry below describes one MapLibre layer plus the IHO display category
 * it belongs to (Display Base, Display Standard, All Other Information), so a
 * single category toggle can hide or show the whole group.
 *
 * Phase 1 keeps the conditional symbology light. The full S-52 rule set is
 * phase 3.
 *
 * Source pattern: every cell adds one source named cell-<cellId>. Each layer
 * uses MapLibre's `source-layer`-less GeoJSON style and filters by
 * properties.objClass / properties.group, so the same layer definitions render
 * any number of cells (quilted).
 */
import type { ExpressionSpecification, FilterSpecification, LayerSpecification } from 'maplibre-gl';
 
import type { DeclutterLevel, Palette, SymbolSetId } from '@/store';
import { getPalette } from './palettes';

/** The three IHO display categories, toggled as layer groups. */
export type DisplayCategory = 'base' | 'standard' | 'other';

export interface ManagedLayer {
  /** Layer id, must be unique. */
  id: string;
  /** IHO display category — Base / Standard / All Other. */
  category: DisplayCategory;
  /** Human-readable group key used by per-layer toggles. */
  group: string;
  /** Build the MapLibre layer spec for a given source id. */
  build: (sourceId: string, palette: Palette, symbolSet: SymbolSetId) => LayerSpecification;
}

// Filter helpers --------------------------------------------------------------

const grpEq = (g: string): ExpressionSpecification => ['==', ['get', 'group'], g];
const objIn = (...os: string[]): ExpressionSpecification => [
  'match',
  ['get', 'objClass'],
  os,
  true,
  false,
];
const objEq = (o: string): ExpressionSpecification => ['==', ['get', 'objClass'], o];

// --- Per-set area/line colour conventions ------------------------------------
// The day palette is already S-52-ish, so the per-set tints are tuned for 'day';
// at dusk/night we fall back to the palette so those keep working without
// bespoke night colours. These are our own renderings of the standard look, not
// copies of any UKHO publication artwork.
interface AreaColours {
  deep: string;
  medium: string;
  shallow: string;
  drying: string;
  contour: string;
  coast: string;
}
// --- User safety depth for the low-zoom wreck/rock declutter -----------------
// At low zoom a wreck/rock/obstruction shows if it's a precomputed relative hazard
// (relHazard) OR its own charted depth (VALSOU → soundingM) is at/above the user's
// safety depth (shoal enough to matter to this boat) OR we're at the detail zoom.
// So a deeper, non-hazard wreck drops out until zoomed in. Unknown depth (coalesce
// 1e6) never satisfies the depth term, so it stays held by the relHazard/zoom rule.
// Default 1e6 = "show everything by depth" until the boat profile sets a value.
// Live: MapView calls setDeclutterSafetyDepth then re-applies the hazard filters.
let declutterSafetyDepthM = 1e6;
export function setDeclutterSafetyDepth(m: number): void {
  declutterSafetyDepthM = Number.isFinite(m) && m > 0 ? m : 1e6;
}
export function declutterSafetyDepth(): number {
  return declutterSafetyDepthM;
}

// --- Declutter LEVEL (full / medium / minimal) -------------------------------
// The full/minimal ends are just the display-category toggles (Standard + Other
// on, or Base only). The MEDIUM level keeps a bit of everything but roughly halves
// the clutter, in two ways that the category toggles can't express on their own:
//   1. a handful of Standard-category detail layers are hidden even though
//      Standard is on (see MEDIUM_HIDDEN_LAYER_IDS) — spot soundings and light
//      flares, so we keep depth CONTOURS but drop the spot depths, and keep the
//      buoys but drop the light flares;
//   2. the buoy/beacon layers drop their minor marks and show only the
//      significant ones (see significantMarkClause) — "only the large buoys".
// This value is embedded in the buoy/beacon filters at build time (a literal), so
// a change is applied live by rebuilding those filters; the hides go through the
// normal visibility path (isVisible reads the level too).
let declutterLevel: DeclutterLevel = 'full';
export function setDeclutterLevel(level: DeclutterLevel): void {
  if (level === 'full' || level === 'medium' || level === 'minimal') declutterLevel = level;
}
export function getDeclutterLevel(): DeclutterLevel {
  return declutterLevel;
}

// Standard-category detail hidden at the MEDIUM level (kept at full, already gone
// at minimal since Standard is off there): the spot soundings and light flares.
// Depth CONTOURS (l-depcnt) and the buoys/beacons themselves are deliberately NOT
// here, so medium keeps contours and buoyage.
export const MEDIUM_HIDDEN_LAYER_IDS = new Set([
  'l-soundg',
  'l-soundg-2',
  'l-soundg-3',
  'l-soundg-4',
  'l-wreck-sounding',
  'l-light-flare',
]);

// The significant / "large" marks we keep at the medium level: principal laterals,
// cardinals, safe-water and isolated-danger. Minor special (yellow) and unknown
// marks drop out, which is the "only large buoys" thinning.
const SIGNIFICANT_MARK_CATEGORIES = [
  'safe_water', 'isolated_danger', 'ncard', 'ecard', 'scard', 'wcard',
  'port', 'stbd', 'preferred_port', 'preferred_stbd',
];
/** At the medium level, restrict buoy/beacon layers to the significant marks; at
 *  full/minimal there's no extra restriction. Returned as an array so it spreads
 *  straight into a filter's 'all' clause (empty = nothing added). */
function significantMarkClauses(): ExpressionSpecification[] {
  if (declutterLevel !== 'medium') return [];
  return [
    [
      'match',
      ['coalesce', ['get', 'category', ['get', 'attrs']], 'def'],
      SIGNIFICANT_MARK_CATEGORIES,
      true,
      false,
    ],
  ];
}

// --- User depth-area shading contours (Planning) -----------------------------
// The two transitions between the shallow / mid / deep tints, binned by the area's
// deepest charted depth (DRVAL2 → depthMaxM). The ECDIS shallow/deep-contour idea:
// e.g. 5 & 20 close inshore, 20 & 50 offshore. Live: MapView calls setDepthShading
// then re-applies the depth-area filters (the values are literals in the filters).
let shadingShallowM = 5;
let shadingDeepM = 10;
export function setDepthShading(shallowM: number, deepM: number): void {
  if (Number.isFinite(shallowM) && shallowM >= 0) shadingShallowM = shallowM;
  // Keep deep strictly beyond shallow so the mid band never inverts.
  if (Number.isFinite(deepM) && deepM > shadingShallowM) shadingDeepM = deepM;
  else if (shadingDeepM <= shadingShallowM) shadingDeepM = shadingShallowM + 1;
}

// --- Depth-area shading MODE -------------------------------------------------
// How much depth colouring the chart lays down, independent of where the contour
// depths sit:
//   - 'off'        : no depth tints at all — the whole sea draws one flat page
//                    colour. Contour lines and soundings still show; the coloured
//                    bands don't. Cleanest, least busy.
//   - 'safety'     : a single two-tone split at the SHALLOW (safety) contour —
//                    water shoaler than it takes the shallow/danger tint, anything
//                    deeper takes the deep tint. The classic ECDIS safety-contour
//                    look; the mid band collapses into deep.
//   - 'graduated'  : the full three-band ramp (shallow / mid / deep) between the
//                    two contours. The most depth information; the default, and
//                    the behaviour before this control gained a mode.
// The band layers' filters embed this at build time (a literal), so a change is
// applied live by rebuilding the three DEPARE band filters (applyDepthBands).
export type DepthShadingMode = 'off' | 'safety' | 'graduated';
let shadingMode: DepthShadingMode = 'graduated';
export function setDepthShadingMode(m: DepthShadingMode): void {
  if (m === 'off' || m === 'safety' || m === 'graduated') shadingMode = m;
}
export function depthShadingMode(): DepthShadingMode {
  return shadingMode;
}

// The depth areas the bands shade: charted depth areas AND dredged areas. The
// drying layer already treats both as depth areas, and the offline convert step
// fills depthMin/Max for both, so a dredged area (DRGARE) should shade like a
// DEPARE. It used to be left out here (the bands matched DEPARE only), so a
// dredged patch fell through every fill and rendered as an unstyled hole — the
// "depth shading messes up here" blank in the sea.
const depthArea = (): ExpressionSpecification => objIn('DEPARE', 'DRGARE');

// The value we bin an area by: its deepest charted depth (DRVAL2 -> depthMaxM).
// Where that's missing (dredged areas carry only a dredged depth, DRVAL1), fall
// back to the shoalest depth we do have rather than defaulting straight to 0 —
// a 10 m dredged channel binning as "shallowest" would be worse than wrong. Only
// when there's no depth at all do we take 0 (shallow, the cautious side).
const DEPTH_MAX: ExpressionSpecification = [
  'coalesce',
  ['get', 'depthMaxM', ['get', 'attrs']],
  ['get', 'depthMinM', ['get', 'attrs']],
  0,
];
// The shoalest charted depth. A drying area carries a negative one (DRVAL1 < 0);
// those are owned by the dedicated drying layer, so the bands leave them alone
// rather than laying a second fill under the drying tint (which was double-
// painting the drying patches).
const DEPTH_MIN: ExpressionSpecification = ['coalesce', ['get', 'depthMinM', ['get', 'attrs']], 0];

// How deep a genuinely intertidal / drying area is allowed to reach. A drying
// patch dries below chart datum (DRVAL1 < 0) and covers to a shoal depth as the
// tide makes; its deepest charted depth (DRVAL2) sits at or just above datum. Some
// cells hold COARSE depth areas that dry in one corner but then drop away to 20 or
// 30 m of navigable water (DRVAL1 -10, DRVAL2 30). The drying test used to be
// DRVAL1 < 0 with NO upper limit on the deep end, so those were blanketed entirely
// with the green intertidal tint — the green bulged out over water that soundings
// show is 13-22 m deep. Capping the drying band at this reach keeps genuine flats
// and marina basins green, while a deeper-reaching area shades by its depth in the
// bands instead (so its deep water reads as deep, not a green blanket). Fixed (not
// the user's shallow contour) so the drying layer stays static and the bands and
// the drying layer always agree on what counts as drying.
const DRYING_REACH_M = 5;

// Genuinely intertidal / drying: dries below datum AND its deepest charted depth is
// still within the shoal reach above. Reused by both the drying fill and the band
// exclusion so the two never disagree (no green blanket, no unstyled hole).
const IS_DRYING: ExpressionSpecification = ['all', ['<', DEPTH_MIN, 0], ['<=', DEPTH_MAX, DRYING_REACH_M]];
// What the depth bands own: every depth area that is NOT genuinely drying (they
// then bin it by DEPTH_MAX). De Morgan of IS_DRYING, kept as a plain 'any' filter
// (no '!') so it reads cleanly and evaluates the same everywhere.
const NOT_DRYING: ExpressionSpecification = ['any', ['>=', DEPTH_MIN, 0], ['>', DEPTH_MAX, DRYING_REACH_M]];
// A clause that never matches, for a band a mode switches off entirely.
const NEVER: ExpressionSpecification = ['==', ['literal', 0], ['literal', 1]];

/** DEEP band: everything deeper than the deep contour (graduated), deeper than the
 *  shallow/safety contour (safety), or the whole sea (off — one flat colour). */
function depareDeepFilter(): FilterSpecification {
  if (shadingMode === 'off') return ['all', depthArea(), NOT_DRYING, ['>=', DEPTH_MAX, 0]];
  const threshold = shadingMode === 'safety' ? shadingShallowM : shadingDeepM;
  return ['all', depthArea(), NOT_DRYING, ['>', DEPTH_MAX, threshold]];
}
/** MID band: only the graduated ramp uses it (between the two contours); the
 *  safety split and off mode fold it away. */
function depareMediumFilter(): FilterSpecification {
  if (shadingMode !== 'graduated') return ['all', objEq('DEPARE'), NEVER];
  return [
    'all',
    depthArea(),
    NOT_DRYING,
    ['>', DEPTH_MAX, shadingShallowM],
    ['<=', DEPTH_MAX, shadingDeepM],
  ];
}
/** SHALLOW band: shoaler than the shallow contour, for both graduated and safety
 *  modes; off mode draws no shallow tint. */
function depareShallowFilter(): FilterSpecification {
  if (shadingMode === 'off') return ['all', objEq('DEPARE'), NEVER];
  return [
    'all',
    depthArea(),
    NOT_DRYING,
    ['>=', DEPTH_MAX, 0],
    ['<=', DEPTH_MAX, shadingShallowM],
  ];
}

function areaColoursFor(palette: Palette, set: SymbolSetId): AreaColours {
  const p = getPalette(palette);
  const base: AreaColours = {
    deep: p.DEPDW,
    medium: p.DEPMS,
    shallow: p.DEPVS,
    drying: p.DEPIT,
    contour: p.DEPCN,
    coast: p.CSTLN,
  };
  if (palette !== 'day') return base;
  if (set === 'np5011') {
    // Paper / INT1: a mostly-white sea with a faint blue shoaling ribbon, green
    // drying, and a heavier coastline — the traditional paper look.
    return {
      deep: '#FFFFFF',
      medium: '#EAF4FB',
      shallow: '#CFE4F2',
      drying: '#A7CFA0',
      contour: '#5E6B73',
      coast: '#4A3C28',
    };
  }
  if (set === 'np5012') {
    // ECDIS / S-52: the genuine PresLib day colours (CIE table from the e4.0.0
    // .dai). Deep->shallow is the real DEPDW/DEPMS/DEPVS blue ramp (deep water is
    // pale blue, not white), DEPIT the grey-green intertidal, DEPCN the contour
    // grey, CSTLN the coastline.
    return {
      deep: '#C9EDFE', // DEPDW
      medium: '#82CAFF', // DEPMS
      shallow: '#61B7FF', // DEPVS
      drying: '#58AF9C', // DEPIT
      contour: '#768C97', // DEPCN
      coast: '#4C5B63', // CSTLN
    };
  }
  return base;
}
/**
 * Colours for the drying-area cover overlay: the normal drying tint, and the
 * colour a drying patch takes once the tide has covered it (it reads as shallow
 * water). Both follow the active set/palette.
 */
export function dryingCoverColours(palette: Palette, set: SymbolSetId): { drying: string; covered: string } {
  const a = areaColoursFor(palette, set);
  return { drying: a.drying, covered: a.shallow };
}

function contourWidth(set: SymbolSetId): ExpressionSpecification {
  if (set === 'np5011') return ['interpolate', ['linear'], ['zoom'], 8, 0.25, 14, 0.85];
  if (set === 'np5012') return ['interpolate', ['linear'], ['zoom'], 8, 0.35, 14, 1.1];
  return ['interpolate', ['linear'], ['zoom'], 8, 0.3, 14, 1];
}
function contourOpacity(set: SymbolSetId): number {
  return set === 'np5011' ? 0.7 : set === 'np5012' ? 0.95 : 0.85;
}
function coastWidth(set: SymbolSetId): ExpressionSpecification {
  if (set === 'np5011') return ['interpolate', ['linear'], ['zoom'], 6, 0.6, 14, 1.8];
  return ['interpolate', ['linear'], ['zoom'], 6, 0.4, 14, 1.4];
}

// Soundings per set. Paper (INT1): black, sloping figures (italic font, falling
// back to upright if the glyph server lacks an italic). ECDIS (S-52): upright,
// dangerous soundings bold, deeper ones lighter. Current: our dim/dark depth-cued
// pair from the palette.
//
// ECDIS keeps the S-52 convention of a bold figure for the shallow/dangerous
// soundings and a lighter one for the deeper. Real ECDIS ships a separate colour
// table per day/dusk/night; the earlier version hardcoded the DAY colours (a
// near-black figure), which all but vanished over the dark dusk/night depth
// bands. We now pick a light figure on the dark palettes so it reads across all
// three, and pair it with a contrasting halo (below).
export function soundingColour(palette: Palette, set: SymbolSetId): ExpressionSpecification {
  const p = getPalette(palette);
  let shallow: string;
  let deep: string;
  if (set === 'np5011') {
    shallow = '#1d1d1d';
    deep = '#3c3c3c';
  } else if (set === 'np5012') {
    if (palette === 'day') {
      shallow = '#000000';
      deep = '#5B6B73';
    } else if (palette === 'dusk') {
      shallow = '#EEF3F6';
      deep = '#9BA8B0';
    } else {
      // night
      shallow = '#E6ECEF';
      deep = '#6E7C86';
    }
  } else {
    shallow = p.SNDG2;
    deep = p.SNDG1;
  }
  return [
    'case',
    ['<', ['coalesce', ['get', 'depthM', ['get', 'attrs']], 99], 5],
    shallow,
    deep,
  ] as ExpressionSpecification;
}

// Halo behind a sounding figure. For ECDIS this has to be the opposite tone to
// the figure so the number keeps a clean edge over whatever depth band sits under
// it: a light halo on the day blue ramp, a dark halo on the dusk/night sea. Other
// sets keep the deep-water tint as before, which already contrasts their figures.
export function soundingHaloColour(palette: Palette, set: SymbolSetId): string {
  const p = getPalette(palette);
  if (set === 'np5012') {
    if (palette === 'day') return '#FFFFFF';
    if (palette === 'dusk') return '#1B242B';
    return '#04070A'; // night
  }
  return p.DEPDW;
}

// A slightly wider halo on ECDIS so the figure separates cleanly from the busier
// blue bands; the other sets keep the thin halo.
export function soundingHaloWidth(set: SymbolSetId): number {
  return set === 'np5012' ? 1.6 : 1;
}

// Sounding figure size. ECDIS gets a small bump because the S-52 blue bands make
// the figures harder to pick out than the paper set's near-white sea.
function soundingTextSize(set: SymbolSetId): ExpressionSpecification {
  if (set === 'np5012') {
    return ['interpolate', ['linear'], ['zoom'], 12, 10, 16, 13] as ExpressionSpecification;
  }
  return ['interpolate', ['linear'], ['zoom'], 12, 9, 16, 12] as ExpressionSpecification;
}
// One safe font stack for soundings across all sets. (A true INT1 sloping figure
// needs an italic face, which the glyph server doesn't carry, so the per-set
// distinction is colour + size, not slope.)
const SOUNDING_FONT: string[] = ['Open Sans Regular'];

// Magenta dashed lines (recommended tracks, fairways, harbour areas). Paper draws
// a finer pecked line; ECDIS a longer dash; Current our medium dash.
function magentaDash(set: SymbolSetId): [number, number] {
  if (set === 'np5011') return [4, 2];
  if (set === 'np5012') return [6, 3];
  return [5, 3];
}

// Per-set land detail colours (day only; palette fallback otherwise). Paper (INT1)
// = warm buff land; ECDIS (S-52) = the greyer tan; Current = palette.
interface LandColours { land: string; builtup: string; building: string; river: string; }
function landColoursFor(palette: Palette, set: SymbolSetId): LandColours {
  const p = getPalette(palette);
  const base: LandColours = { land: p.LANDA, builtup: p.CHGRD, building: p.CHGRD, river: p.DEPVS };
  if (palette !== 'day') return base;
  if (set === 'np5011') return { land: '#F1E2A6', builtup: '#CBBF99', building: '#BCAE84', river: '#EAF4FB' };
  if (set === 'np5012') return { land: '#BFBE8F', builtup: '#B2B184', building: '#A5A478', river: '#A7D9FB' }; // LANDA + DEPMD river
  return base;
}
// Place-name / label ink per set (day only): Paper black, ECDIS dark slate.
function labelColourFor(palette: Palette, set: SymbolSetId): string {
  const p = getPalette(palette);
  if (palette !== 'day') return p.CHGRD;
  if (set === 'np5011') return '#1d1d1d';
  if (set === 'np5012') return '#4C5B63'; // CHGRD
  return p.CHGRD;
}

// SCAMIN → minzoom heuristic, kept here for reference. S-57 SCAMIN values are
// scales (e.g. 30000) meaning "only draw at scales smaller than this". MapLibre's
// minzoom works the other way (don't draw below this zoom). The conversion is
// rough because the relationship between scale and zoom level depends on display
// DPI, so we default to layer-level minzoom values and trust the converter's
// SCAMIN to be sensible. Per-feature SCAMIN filtering can be wired later.

// ----------------------------------------------------------------------------
// Layer definitions, painted bottom -> top in the order below.
// ----------------------------------------------------------------------------

export const LAYERS: ManagedLayer[] = [
  // --- Depth area fills (Display Base) ---------------------------------------
  {
    id: 'l-depare-deep',
    category: 'base',
    group: 'depth-areas',
    build: (src, palette, set) => ({
      id: `${src}-depare-deep`,
      type: 'fill',
      source: src,
      filter: depareDeepFilter(),
      paint: { 'fill-color': areaColoursFor(palette, set).deep },
    }),
  },
  {
    id: 'l-depare-medium',
    category: 'base',
    group: 'depth-areas',
    build: (src, palette, set) => ({
      id: `${src}-depare-medium`,
      type: 'fill',
      source: src,
      filter: depareMediumFilter(),
      paint: { 'fill-color': areaColoursFor(palette, set).medium },
    }),
  },
  {
    id: 'l-depare-shallow',
    category: 'base',
    group: 'depth-areas',
    build: (src, palette, set) => ({
      id: `${src}-depare-shallow`,
      type: 'fill',
      source: src,
      filter: depareShallowFilter(),
      paint: { 'fill-color': areaColoursFor(palette, set).shallow },
    }),
  },
  {
    id: 'l-depare-drying',
    category: 'base',
    group: 'depth-areas',
    build: (src, palette, set) => ({
      id: `${src}-depare-drying`,
      type: 'fill',
      source: src,
      filter: ['all', objIn('DEPARE', 'DRGARE'), IS_DRYING],
      paint: { 'fill-color': areaColoursFor(palette, set).drying },
    }),
  },

  // --- Depth contours (Display Standard) ------------------------------------
  {
    id: 'l-depcnt',
    category: 'standard',
    group: 'depth-contours',
    build: (src, palette, set) => ({
      id: `${src}-depcnt`,
      type: 'line',
      source: src,
      filter: ['==', ['get', 'objClass'], 'DEPCNT'],
      minzoom: 9,
      paint: {
        'line-color': areaColoursFor(palette, set).contour,
        'line-width': contourWidth(set),
        'line-opacity': contourOpacity(set),
      },
    }),
  },
  // Safety-contour highlight was wired here as a feature-state-driven layer,
  // but MapLibre doesn't allow feature-state inside filters. Removed for now.
  // The proper approach is to compare VALDCO against the user's safety contour
  // setting using a 'case' paint expression with a Zustand-driven value. We'll
  // wire that when the safety panel needs it.

  // --- Land fill + coastline (Display Base) ---------------------------------
  {
    id: 'l-land-fill',
    category: 'base',
    group: 'land',
    build: (src, palette, set) => ({
      id: `${src}-land-fill`,
      type: 'fill',
      source: src,
      filter: objEq('LNDARE'),
      paint: { 'fill-color': landColoursFor(palette, set).land },
    }),
  },
  {
    id: 'l-coastline',
    category: 'base',
    group: 'land',
    build: (src, palette, set) => ({
      id: `${src}-coastline`,
      type: 'line',
      source: src,
      filter: objIn('LNDARE', 'COALNE'),
      paint: {
        'line-color': areaColoursFor(palette, set).coast,
        'line-width': coastWidth(set),
      },
    }),
  },

  // --- Built-up areas: a subtle urban tint so towns read as built-up ---------
  {
    id: 'l-builtup-fill',
    category: 'standard',
    group: 'land',
    build: (src, palette, set) => ({
      id: `${src}-builtup-fill`,
      type: 'fill',
      source: src,
      filter: ['all', objEq('BUAARE'), ['==', ['geometry-type'], 'Polygon']],
      minzoom: 8,
      paint: { 'fill-color': landColoursFor(palette, set).builtup, 'fill-opacity': 0.35 },
    }),
  },

  // --- Rivers and inland water ----------------------------------------------
  {
    id: 'l-river-fill',
    category: 'base',
    group: 'land',
    build: (src, palette, set) => ({
      id: `${src}-river-fill`,
      type: 'fill',
      source: src,
      filter: ['all', objEq('RIVERS'), ['==', ['geometry-type'], 'Polygon']],
      minzoom: 9,
      paint: { 'fill-color': landColoursFor(palette, set).river },
    }),
  },
  {
    id: 'l-river-line',
    category: 'base',
    group: 'land',
    build: (src, palette) => ({
      id: `${src}-river-line`,
      type: 'line',
      source: src,
      filter: ['all', objEq('RIVERS'), ['==', ['geometry-type'], 'LineString']],
      minzoom: 9,
      paint: {
        'line-color': getPalette(palette).DEPCN,
        'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.6, 14, 2],
      },
    }),
  },

  // --- Roads and railways (land orientation detail, higher zoom) ------------
  {
    id: 'l-road',
    category: 'other',
    group: 'roads',
    build: (src, palette) => ({
      id: `${src}-road`,
      type: 'line',
      source: src,
      filter: objEq('ROADWY'),
      minzoom: 12,
      paint: {
        'line-color': getPalette(palette).CHGRD,
        'line-width': ['interpolate', ['linear'], ['zoom'], 12, 0.5, 16, 1.6],
        'line-opacity': 0.6,
      },
    }),
  },
  {
    id: 'l-railway',
    category: 'other',
    group: 'roads',
    build: (src, palette) => ({
      id: `${src}-railway`,
      type: 'line',
      source: src,
      filter: objEq('RAILWY'),
      minzoom: 12,
      paint: {
        'line-color': getPalette(palette).CHGRD,
        'line-width': 1,
        'line-dasharray': [3, 2],
        'line-opacity': 0.6,
      },
    }),
  },

  // --- Building footprints (polygons only, high zoom) -----------------------
  {
    id: 'l-building',
    category: 'other',
    group: 'buildings',
    build: (src, palette, set) => ({
      id: `${src}-building`,
      type: 'fill',
      source: src,
      filter: ['all', objEq('BUISGL'), ['==', ['geometry-type'], 'Polygon']],
      minzoom: 14,
      paint: {
        'fill-color': landColoursFor(palette, set).building,
        'fill-opacity': 0.55,
        'fill-outline-color': getPalette(palette).CHBLK,
      },
    }),
  },

  // --- Harbour works: pontoons, bridges, harbour areas ----------------------
  {
    id: 'l-pontoon',
    category: 'standard',
    group: 'harbour',
    build: (src, palette) => ({
      id: `${src}-pontoon`,
      type: 'line',
      source: src,
      filter: objEq('PONTON'),
      minzoom: 13,
      paint: {
        'line-color': getPalette(palette).CHGRD,
        'line-width': ['interpolate', ['linear'], ['zoom'], 13, 1, 17, 3],
      },
    }),
  },
  {
    id: 'l-bridge',
    category: 'standard',
    group: 'harbour',
    build: (src, palette) => ({
      id: `${src}-bridge`,
      type: 'line',
      source: src,
      filter: objEq('BRIDGE'),
      minzoom: 12,
      paint: { 'line-color': getPalette(palette).CHBLK, 'line-width': 1.6 },
    }),
  },
  {
    id: 'l-harbour-area',
    category: 'other',
    group: 'harbour',
    build: (src, palette, set) => ({
      id: `${src}-harbour-area`,
      type: 'line',
      source: src,
      filter: ['all', objEq('HRBFAC'), ['==', ['geometry-type'], 'Polygon']],
      minzoom: 13,
      paint: {
        'line-color': getPalette(palette).CHMGD,
        'line-width': 1,
        'line-dasharray': magentaDash(set),
        'line-opacity': 0.5,
      },
    }),
  },

  // --- Shoreline construction (jetties, breakwaters etc.) --------------------
  {
    id: 'l-slcons',
    category: 'standard',
    group: 'shoreline',
    build: (src, palette) => ({
      id: `${src}-slcons`,
      type: 'line',
      source: src,
      filter: objEq('SLCONS'),
      minzoom: 10,
      paint: {
        'line-color': getPalette(palette).CHBLK,
        'line-width': 1.2,
      },
    }),
  },

  // --- Soundings (Display Standard) -----------------------------------------
  {
    // Soundings arrive in four tiers by local shallowest-first rank (sdRank,
    // stamped at load, on a depth-banded grid — see markDensity.ts): the
    // shallowest in each grid square first, a couple more a zoom later, the
    // full survey only when zoomed right in. One layer showed everything from
    // z12, and on a dense NOAA harbour cell that was a wall of numbers the
    // moment soundings switched on. Three tiers still had a cliff: everything
    // past rank 2 arrived at once at z13.8, which is how the same harbour read
    // "not enough depths" at z13.77 and "too many depths" at z14.28 (the Ponce
    // reports). The fourth tier holds the deep end of each bucket back to z15.
    // The tiers are separate layers because a filter cannot watch the zoom
    // continuously, but minzoom can.
    id: 'l-soundg',
    category: 'standard',
    group: 'soundings',
    build: (src, palette, set) => ({
      id: `${src}-soundg`,
      type: 'symbol',
      source: src,
      filter: ['all', grpEq('sounding'), ['<=', ['coalesce', ['get', 'sdRank'], 0], 0]],
      minzoom: 12,
      layout: {
        'text-field': [
          'number-format',
          ['get', 'depthM', ['get', 'attrs']],
          { 'min-fraction-digits': 0, 'max-fraction-digits': 1 },
        ],
        'text-font': SOUNDING_FONT,
        'text-size': soundingTextSize(set),
        'text-allow-overlap': false,
        'text-ignore-placement': false,
      },
      paint: {
        'text-color': soundingColour(palette, set),
        'text-halo-color': soundingHaloColour(palette, set),
        'text-halo-width': soundingHaloWidth(set),
      },
    }),
  },

  {
    id: 'l-soundg-2',
    category: 'standard',
    group: 'soundings',
    build: (src, palette, set) => ({
      id: `${src}-soundg-2`,
      type: 'symbol',
      source: src,
      filter: [
        'all',
        grpEq('sounding'),
        ['>=', ['coalesce', ['get', 'sdRank'], 0], 1],
        ['<=', ['coalesce', ['get', 'sdRank'], 0], 2],
      ],
      minzoom: 13,
      layout: {
        'text-field': [
          'number-format',
          ['get', 'depthM', ['get', 'attrs']],
          { 'min-fraction-digits': 0, 'max-fraction-digits': 1 },
        ],
        'text-font': SOUNDING_FONT,
        'text-size': soundingTextSize(set),
        'text-allow-overlap': false,
        'text-ignore-placement': false,
      },
      paint: {
        'text-color': soundingColour(palette, set),
        'text-halo-color': soundingHaloColour(palette, set),
        'text-halo-width': soundingHaloWidth(set),
      },
    }),
  },
  {
    id: 'l-soundg-3',
    category: 'standard',
    group: 'soundings',
    build: (src, palette, set) => ({
      id: `${src}-soundg-3`,
      type: 'symbol',
      source: src,
      filter: [
        'all',
        grpEq('sounding'),
        ['>=', ['coalesce', ['get', 'sdRank'], 0], 3],
        ['<=', ['coalesce', ['get', 'sdRank'], 0], 5],
      ],
      minzoom: 13.8,
      layout: {
        'text-field': [
          'number-format',
          ['get', 'depthM', ['get', 'attrs']],
          { 'min-fraction-digits': 0, 'max-fraction-digits': 1 },
        ],
        'text-font': SOUNDING_FONT,
        'text-size': soundingTextSize(set),
        'text-allow-overlap': false,
        'text-ignore-placement': false,
      },
      paint: {
        'text-color': soundingColour(palette, set),
        'text-halo-color': soundingHaloColour(palette, set),
        'text-halo-width': soundingHaloWidth(set),
      },
    }),
  },
  {
    // The deep end of every bucket (ranks 6+, always the DEEPEST near-equals,
    // since ranking is shallowest-first). Held back to z15 so crossing z13.8
    // no longer opens the firehose in one step.
    id: 'l-soundg-4',
    category: 'standard',
    group: 'soundings',
    build: (src, palette, set) => ({
      id: `${src}-soundg-4`,
      type: 'symbol',
      source: src,
      filter: ['all', grpEq('sounding'), ['>=', ['coalesce', ['get', 'sdRank'], 0], 6]],
      minzoom: 15,
      layout: {
        'text-field': [
          'number-format',
          ['get', 'depthM', ['get', 'attrs']],
          { 'min-fraction-digits': 0, 'max-fraction-digits': 1 },
        ],
        'text-font': SOUNDING_FONT,
        'text-size': soundingTextSize(set),
        'text-allow-overlap': false,
        'text-ignore-placement': false,
      },
      paint: {
        'text-color': soundingColour(palette, set),
        'text-halo-color': soundingHaloColour(palette, set),
        'text-halo-width': soundingHaloWidth(set),
      },
    }),
  },
  // Contour depth labels, along the line the way a paper chart writes them.
  // These show well before the soundings do, which is what keeps a zoomed-out
  // chart honest: no individual depths, but every contour says what it is, so
  // "inside the 10 m line" is still readable at passage-planning scale.
  {
    id: 'l-depcnt-label',
    category: 'standard',
    group: 'depth-contours',
    build: (src, palette, set) => ({
      id: `${src}-depcnt-label`,
      type: 'symbol',
      source: src,
      filter: ['==', ['get', 'objClass'], 'DEPCNT'],
      minzoom: 8.5,
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 400,
        'text-field': [
          'number-format',
          ['get', 'depthM', ['get', 'attrs']],
          { 'min-fraction-digits': 0, 'max-fraction-digits': 1 },
        ],
        'text-font': SOUNDING_FONT,
        'text-size': 10,
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': areaColoursFor(palette, set).contour,
        'text-halo-color': soundingHaloColour(palette, set),
        'text-halo-width': 1.2,
      },
    }),
  },

  // --- Obstructions / underwater rocks (Display Standard) --------------------
  // Decluttered and typed: foul-ground areas render as areas (not a hundred
  // dots), surface-breaking rocks (awash / covering) show first and bolder,
  // always-submerged rocks come in only when zoomed further. Rocks are black /
  // grey (magenta is reserved for lights).
  {
    id: 'l-foul-area',
    category: 'standard',
    group: 'obstructions',
    build: (src, palette, set) => ({
      id: `${src}-foul-area`,
      type: 'fill',
      source: src,
      filter: ['all', objEq('OBSTRN'), ['==', ['geometry-type'], 'Polygon']],
      minzoom: 9,
      paint: {
        'fill-color': getPalette(palette).CHGRD,
        'fill-opacity': set === 'np5011' ? 0.08 : set === 'np5012' ? 0.18 : 0.14,
      },
    }),
  },
  {
    id: 'l-foul-edge',
    category: 'standard',
    group: 'obstructions',
    build: (src, palette, set) => ({
      id: `${src}-foul-edge`,
      type: 'line',
      source: src,
      filter: ['all', objEq('OBSTRN'), ['==', ['geometry-type'], 'Polygon']],
      minzoom: 9,
      paint: {
        // Paper: a black dotted boundary. ECDIS: a grey dashed boundary.
        'line-color': set === 'np5012' ? getPalette(palette).CHGRD : getPalette(palette).CHBLK,
        'line-width': 1,
        'line-dasharray': set === 'np5011' ? [1, 2.5] : set === 'np5012' ? [3, 2] : [1, 2],
        'line-opacity': 0.7,
      },
    }),
  },
  {
    id: 'l-rock-danger',
    category: 'standard',
    group: 'obstructions',
    build: (src) => ({
      id: `${src}-rock-danger`,
      type: 'symbol',
      source: src,
      // Awash / covering rocks and obstructions, the ones that break the
      // surface: shown earlier and a touch bolder.
      filter: [
        'all',
        objIn('OBSTRN', 'UWTROC'),
        ['==', ['geometry-type'], 'Point'],
        ['in', ['to-number', ['coalesce', ['get', 'WATLEV', ['get', 'raw', ['get', 'attrs']]], -1]], ['literal', [4, 5]]],
      ],
      minzoom: 10,
      layout: {
        'icon-image': 'chart-rock-awash',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 0.95],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    }),
  },
  {
    id: 'l-rock-submerged',
    category: 'standard',
    group: 'obstructions',
    build: (src) => ({
      id: `${src}-rock-submerged`,
      type: 'symbol',
      source: src,
      // Always-submerged rocks / obstructions: the bulk, so only at closer zoom.
      filter: [
        'all',
        objEq('UWTROC'),
        ['==', ['geometry-type'], 'Point'],
        ['!', ['in', ['to-number', ['coalesce', ['get', 'WATLEV', ['get', 'raw', ['get', 'attrs']]], -1]], ['literal', [4, 5]]]],
        // Submerged rocks: the user's safety depth governs — one shoaler than/equal
        // to it shows zoomed out; a deeper or unknown-depth one holds until the
        // detail zoom ~12. Awash/covering rocks are the l-rock-danger layer (always).
        [
          'any',
          // Safety depth governs: a submerged rock/obstruction shoaler than/equal to
          // it shows; a known-deeper or unknown-depth one holds to the detail zoom.
          // (Awash/covering ones are the l-rock-danger layer and always show.)
          ['<=', ['coalesce', ['get', 'soundingM', ['get', 'attrs']], 1e6], declutterSafetyDepthM],
          ['>=', ['zoom'], 12],
        ],
      ],
      minzoom: 9,
      layout: {
        'icon-image': 'chart-rock-sub',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 9, 0.4, 16, 0.8],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    }),
  },

  {
    // Generic obstruction shown as a point (OBSTRN points that are not covering /
    // awash rocks). Genuine S-52 OBSTRN01 for 'paper'/'ecdis', our dotted danger
    // circle for 'current'. Same safety-depth declutter as submerged rocks: one
    // shoaler than/equal to the safety depth shows zoomed out, the rest hold to ~12.
    id: 'l-obstruction-point',
    category: 'standard',
    group: 'obstructions',
    build: (src) => ({
      id: `${src}-obstruction-point`,
      type: 'symbol',
      source: src,
      filter: [
        'all',
        objEq('OBSTRN'),
        ['==', ['geometry-type'], 'Point'],
        ['!', ['in', ['to-number', ['coalesce', ['get', 'WATLEV', ['get', 'raw', ['get', 'attrs']]], -1]], ['literal', [4, 5]]]],
        [
          'any',
          // Safety depth governs: a submerged rock/obstruction shoaler than/equal to
          // it shows; a known-deeper or unknown-depth one holds to the detail zoom.
          // (Awash/covering ones are the l-rock-danger layer and always show.)
          ['<=', ['coalesce', ['get', 'soundingM', ['get', 'attrs']], 1e6], declutterSafetyDepthM],
          ['>=', ['zoom'], 12],
        ],
      ],
      minzoom: 9,
      layout: {
        'icon-image': 'chart-obstruction',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 9, 0.45, 16, 0.85],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    }),
  },

  // --- Wrecks (Display Base — these matter) ---------------------------------
  {
    id: 'l-wrecks',
    category: 'base',
    group: 'wrecks',
    build: (src) => ({
      id: `${src}-wrecks`,
      type: 'symbol',
      source: src,
      filter: [
        'all',
        grpEq('wreck'),
        // Low-zoom declutter governed by the user's SAFETY DEPTH (boat profile).
        // A wreck shows zoomed OUT only if:
        //   (a) it breaks the surface — hull/masts showing (CATWRK 4/5) or covers /
        //       awash (WATLEV 4/5): a collision hazard at any interest depth; OR
        //   (b) its own charted depth (VALSOU → soundingM) is at/above the safety
        //       depth, so it is shoal enough to matter to this boat; OR
        //   (c) we are at the detail zoom (~12), which shows everything.
        // So a KNOWN-deeper wreck drops out at low zoom even if it shoals above its
        // surroundings, and an unknown-depth wreck (coalesce 1e6, never ≤ safety)
        // holds — not known-shallow-enough to matter. The relHazard precompute is no
        // longer used here; the safety depth is the primary low-zoom filter. Nothing
        // is ever hidden once zoomed in.
        [
          'any',
          ['in', ['to-number', ['coalesce', ['get', 'wreckCategory', ['get', 'attrs']], 0]], ['literal', [4, 5]]],
          ['in', ['to-number', ['coalesce', ['get', 'WATLEV', ['get', 'raw', ['get', 'attrs']]], -1]], ['literal', [4, 5]]],
          ['<=', ['coalesce', ['get', 'soundingM', ['get', 'attrs']], 1e6], declutterSafetyDepthM],
          ['>=', ['zoom'], 12],
        ],
      ],
      minzoom: 9,
      layout: {
        // Pick the symbol by S-57 wreck category (CATWRK, exposed as wreckCategory):
        // 1 non-dangerous, 4 masts showing, 5 hull showing, 2/other dangerous.
        'icon-image': [
          'match',
          ['to-number', ['coalesce', ['get', 'wreckCategory', ['get', 'attrs']], 2]],
          1, 'chart-wreck-nondangerous',
          4, 'chart-wreck-masts',
          5, 'chart-wreck-hull',
          'chart-wreck-dangerous',
        ],
        'icon-size': ['interpolate', ['linear'], ['zoom'], 9, 0.5, 16, 0.95],
        // Never collide-drop a wreck; thinning is by the zoom filter above.
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    }),
  },

  // Wreck sounding: the charted depth over the wreck (VALSOU → soundingM), shown
  // under the symbol where a depth is known. Swept depth (QUASOU 7) carries no
  // line-over here — the glyph server can't draw the over-bar; flagged.
  {
    id: 'l-wreck-sounding',
    category: 'standard',
    group: 'wrecks',
    build: (src, palette, set) => ({
      id: `${src}-wreck-sounding`,
      type: 'symbol',
      source: src,
      filter: ['all', grpEq('wreck'), ['has', 'soundingM', ['get', 'attrs']]],
      minzoom: 13,
      layout: {
        'text-field': [
          'number-format',
          ['get', 'soundingM', ['get', 'attrs']],
          { 'min-fraction-digits': 0, 'max-fraction-digits': 1 },
        ],
        'text-font': SOUNDING_FONT,
        'text-size': ['interpolate', ['linear'], ['zoom'], 13, 9, 16, 11],
        'text-offset': [0, 1.6],
        'text-anchor': 'top',
        'text-optional': true,
      },
      paint: {
        'text-color': soundingColour(palette, set),
        'text-halo-color': soundingHaloColour(palette, set),
        'text-halo-width': soundingHaloWidth(set),
      },
    }),
  },

  // --- Buoyage --------------------------------------------------------------
  // Proper S-52-lite buoyage symbols by IALA category (chartSymbols.ts draws and
  // supplies each image on demand). Lateral can/cone shapes, cardinal cone
  // topmarks with colour bands, safe-water, isolated-danger and special marks,
  // all driven by the real `category` attribute the converter normalises.
  {
    id: 'l-buoy-dot',
    category: 'base',
    group: 'buoyage',
    build: (src) => ({
      id: `${src}-buoy-dot`,
      type: 'symbol',
      source: src,
      filter: [
        'all',
        grpEq('buoy'),
        // Scale-dependent thinning, zoomed-OUT only (cells carry no SCAMIN, so we
        // gate by IALA importance). Principal + lateral marks always show; minor /
        // unknown marks appear from a higher zoom. By ~z11 every mark is visible,
        // so zoomed in NOTHING is hidden. Thinning is by this filter, never by
        // collision (icon-allow-overlap stays on).
        //
        // Density-aware: a mark sitting on its own (few neighbours within ~1.5 nm,
        // stamped by annotateMarkDensity) drops to gate 0 so it shows at low zoom
        // even if it's a minor / special mark. Only clustered marks keep the
        // category gate, so the thinning still works where it's actually crowded.
        // No neighbours value (coalesce 99) means "treat as clustered", so any
        // source without the hint behaves exactly as before.
        [
          '>=',
          ['zoom'],
          [
            'case',
            ['<=', ['to-number', ['coalesce', ['get', 'neighbours', ['get', 'attrs']], 99]], 2],
            0,
            [
              'match',
              ['coalesce', ['get', 'category', ['get', 'attrs']], 'def'],
              ['safe_water', 'isolated_danger', 'ncard', 'ecard', 'scard', 'wcard', 'port', 'stbd', 'preferred_port', 'preferred_stbd'],
              0,
              ['special'],
              11,
              10,
            ],
          ],
        ],
        ...significantMarkClauses(),
      ],
      minzoom: 8,
      layout: {
        // Buoy icon id carries the IALA category, the S-57 body shape (BOYSHP)
        // when present, and the charted COLOUR when present:
        // chart-mark-<category>[--<shape>][~<colours>]. The colour suffix is what
        // keeps IALA region B honest: CATLAM says port/starboard by DIRECTION of
        // buoyage, but the paint differs by region (region A port = red can;
        // region B port = green can), so drawing region-A colours from the
        // category alone painted every US lateral mark the wrong colour — a red
        // can flashing Fl.G at Ponce. COLOUR carries the actual paint, so the
        // renderer banded from it is right in both regions.
        'icon-image': [
          'concat',
          'chart-mark-',
          ['coalesce', ['get', 'category', ['get', 'attrs']], 'def'],
          [
            'case',
            ['has', 'shape', ['get', 'attrs']],
            ['concat', '--', ['get', 'shape', ['get', 'attrs']]],
            '',
          ],
          [
            'case',
            ['has', 'colour', ['get', 'attrs']],
            ['concat', '~', ['get', 'colour', ['get', 'attrs']]],
            '',
          ],
        ],
        'icon-size': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 14, 0.95],
        'icon-anchor': 'bottom',
        // Never collide-drop a mark: a lit buoy must show its body AND its flare,
        // and nothing may be hidden when zoomed in. Thinning is the zoom filter
        // above, not collision.
        'icon-allow-overlap': true,
        // Marks are ALWAYS drawn (allow-overlap stays true, thinning is by the
        // filter above, never by collision). But they no longer ignore placement,
        // so they reserve their footprint against the label layers. Without that a
        // light characteristic offset from its own mark can be laid down a pixel
        // from the NEXT mark's position dot, which reads as the wrong light on the
        // wrong buoy. Labels are text-optional, so the loser is the label, not the
        // mark.
        'icon-ignore-placement': false,
      },
    }),
  },
  {
    // Beacons are FIXED marks (BCN* in S-57), not floating buoys, so they get
    // their own glyph: genuine S-52 simplified beacon symbols for 'paper'/'ecdis'
    // (a rigid coloured stick / cardinal topmark, distinct from a floating buoy),
    // falling back to the buoy mark for the 'current' set. Same category attribute
    // and the same zoom-thinning as buoys; nothing collides, so zoomed in every
    // beacon shows.
    id: 'l-beacon-dot',
    category: 'base',
    group: 'buoyage',
    build: (src) => ({
      id: `${src}-beacon-dot`,
      type: 'symbol',
      source: src,
      filter: [
        'all',
        grpEq('beacon'),
        // Same density-aware gate as buoys: a lone beacon shows at low zoom, a
        // clustered minor one keeps its category gate. (See l-buoy-dot.)
        [
          '>=',
          ['zoom'],
          [
            'case',
            ['<=', ['to-number', ['coalesce', ['get', 'neighbours', ['get', 'attrs']], 99]], 2],
            0,
            [
              'match',
              ['coalesce', ['get', 'category', ['get', 'attrs']], 'def'],
              ['safe_water', 'isolated_danger', 'ncard', 'ecard', 'scard', 'wcard', 'port', 'stbd', 'preferred_port', 'preferred_stbd'],
              0,
              ['special'],
              11,
              10,
            ],
          ],
        ],
        ...significantMarkClauses(),
      ],
      minzoom: 8,
      layout: {
        // Same colour suffix as buoys (see l-buoy-dot): the charted COLOUR keeps
        // region-B lateral beacons the right colour, not the region-A default.
        'icon-image': [
          'concat',
          'chart-beacon-',
          ['coalesce', ['get', 'category', ['get', 'attrs']], 'def'],
          [
            'case',
            ['has', 'colour', ['get', 'attrs']],
            ['concat', '~', ['get', 'colour', ['get', 'attrs']]],
            '',
          ],
        ],
        'icon-size': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 14, 0.95],
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        // Reserve the mark's footprint against the label layers, so a light
        // characteristic offset from its own mark can never be laid down on the
        // NEXT mark's position dot. Still always drawn (allow-overlap is true);
        // labels are text-optional, so the loser is the label, not the mark.
        'icon-ignore-placement': false,
      },
    }),
  },
  {
    id: 'l-buoy-label',
    category: 'other',
    group: 'buoyage',
    build: (src, palette) => ({
      id: `${src}-buoy-label`,
      type: 'symbol',
      source: src,
      filter: ['any', grpEq('buoy'), grpEq('beacon')],
      minzoom: 12,
      layout: {
        'text-field': ['coalesce', ['get', 'name', ['get', 'attrs']], ''],
        'text-font': ['Open Sans Regular'],
        'text-size': 10,
        'text-offset': [0.9, 0],
        'text-anchor': 'left',
        'text-optional': true,
      },
      paint: {
        'text-color': getPalette(palette).CHBLK,
        'text-halo-color': getPalette(palette).DEPDW,
        'text-halo-width': 1.2,
      },
    }),
  },

  // --- Transits / leading lines --------------------------------------------
  // Charted recommended tracks (RECTRC) and navigation / leading lines (NAVLNE)
  // straight from the cell, dashed, with the line's orientation labelled.
  {
    id: 'l-transit-line',
    category: 'standard',
    group: 'transits',
    build: (src, palette, set) => ({
      id: `${src}-transit-line`,
      type: 'line',
      source: src,
      filter: objIn('RECTRC', 'NAVLNE'),
      minzoom: 9,
      paint: {
        'line-color': getPalette(palette).CHMGD,
        'line-width': 1.2,
        'line-dasharray': magentaDash(set),
        'line-opacity': 0.9,
      },
    }),
  },
  {
    id: 'l-transit-label',
    category: 'other',
    group: 'transits',
    build: (src, palette) => ({
      id: `${src}-transit-label`,
      type: 'symbol',
      source: src,
      filter: [
        'all',
        objIn('RECTRC', 'NAVLNE'),
        ['>', ['coalesce', ['get', 'ORIENT', ['get', 'raw', ['get', 'attrs']]], 0], 0],
      ],
      minzoom: 11,
      layout: {
        'symbol-placement': 'line-center',
        'text-field': [
          'concat',
          ['to-string', ['round', ['coalesce', ['get', 'ORIENT', ['get', 'raw', ['get', 'attrs']]], 0]]],
          '\u00B0',
        ],
        'text-font': ['Open Sans Regular'],
        'text-size': 10,
        'text-rotation-alignment': 'map',
        'text-keep-upright': true,
      },
      paint: {
        'text-color': getPalette(palette).CHMGD,
        'text-halo-color': getPalette(palette).DEPDW,
        'text-halo-width': 1.4,
      },
    }),
  },

  // --- Lights --------------------------------------------------------------
  {
    id: 'l-light-flare',
    category: 'standard',
    group: 'lights',
    build: (src) => ({
      id: `${src}-light-flare`,
      type: 'symbol',
      source: src,
      filter: [
        'all',
        grpEq('light'),
        // Thin lights zoomed OUT by nominal range: major lights always show, minor
        // / unranged lights appear from a higher zoom. All visible by ~z11.
        [
          '>=',
          ['zoom'],
          [
            'case',
            ['>=', ['coalesce', ['get', 'rangeNm', ['get', 'attrs']], 0], 10], 0,
            ['>=', ['coalesce', ['get', 'rangeNm', ['get', 'attrs']], 0], 5], 9,
            11,
          ],
        ],
      ],
      minzoom: 7,
      layout: {
        // Proper S-52-style light flare (chartSymbols.ts), tinted by the light's
        // colour. Anchored at the bottom so the flare rises from the light dot.
        'icon-image': ['concat', 'chart-light-', ['coalesce', ['get', 'colour', ['get', 'attrs']], 'W']],
        'icon-size': ['interpolate', ['linear'], ['zoom'], 7, 0.5, 14, 0.95],
        'icon-anchor': 'bottom',
        // Never collide-drop a flare: a lit mark always shows its flare, and no
        // flare is hidden when zoomed in. Thinning is the zoom filter above.
        'icon-allow-overlap': true,
        // The flare is a big translucent cone rather than a position dot, so it
        // keeps ignoring placement. Making it reserve that footprint would wipe
        // out the labels around every lit mark.
        'icon-ignore-placement': true,
      },
    }),
  },
  // Lighthouse structures: LNDMRK landmarks categorised as light houses
  // (CATLMK 17). Drawn prominently so the major lights read as the marks they
  // are, from a low zoom.
  {
    id: 'l-lighthouse',
    category: 'standard',
    group: 'lights',
    build: (src) => ({
      id: `${src}-lighthouse`,
      type: 'symbol',
      source: src,
      filter: [
        'all',
        objEq('LNDMRK'),
        ['in', '17', ['coalesce', ['get', 'CATLMK', ['get', 'raw', ['get', 'attrs']]], ['literal', []]]],
      ],
      minzoom: 6,
      layout: {
        'icon-image': 'chart-lighthouse',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 6, 0.6, 13, 1.0],
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        // Reserve the mark's footprint against the label layers, so a light
        // characteristic offset from its own mark can never be laid down on the
        // NEXT mark's position dot. Still always drawn (allow-overlap is true);
        // labels are text-optional, so the loser is the label, not the mark.
        'icon-ignore-placement': false,
      },
    }),
  },
  {
    id: 'l-light-character',
    category: 'other',
    group: 'lights',
    build: (src, palette) => ({
      id: `${src}-light-character`,
      type: 'symbol',
      source: src,
      filter: ['all', grpEq('light'), ['has', 'lightCharacter', ['get', 'attrs']]],
      minzoom: 11,
      layout: {
        'text-field': ['get', 'lightCharacter', ['get', 'attrs']],
        'text-font': ['Open Sans Regular'],
        'text-size': 10,
        'text-offset': [1.2, 0.9],
        'text-anchor': 'top-left',
        'text-optional': true,
        // Let the label slide to the other side rather than sit on a neighbour.
        'text-variable-anchor': ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
        'text-radial-offset': 1.1,
      },
      paint: {
        'text-color': getPalette(palette).CHBLK,
        'text-halo-color': getPalette(palette).DEPDW,
        'text-halo-width': 1.2,
      },
    }),
  },

  // --- CATZOC overlay (data quality) — opt-in via a toggle ------------------
  {
    id: 'l-mqual-fill',
    category: 'other',
    group: 'catzoc',
    build: (src, palette) => ({
      id: `${src}-mqual-fill`,
      type: 'fill',
      source: src,
      filter: ['==', ['get', 'objClass'], 'M_QUAL'],
      paint: {
        // CATZOC on M_QUAL polygons is the raw S-57 code (1=A1, 2=A2, 3=B,
        // 4=C, 5=D, 6=U). Match those numbers to the colour bands.
        'fill-color': [
          'match',
          ['to-number', ['coalesce', ['get', 'CATZOC', ['get', 'raw', ['get', 'attrs']]], 6]],
          1, '#2ecc71', // A1
          2, '#f1c40f', // A2
          3, '#e67e22', // B
          4, '#e74c3c', // C
          5, '#9b59b6', // D
          6, '#7f8c8d', // U
          /* default */ palette === 'night' ? '#444' : '#bdc3c7',
        ],
        'fill-opacity': 0.22,
        'fill-outline-color': getPalette(palette).CHGRD,
      },
    }),
  },

  // --- Land names / landmarks (All Other Information) -----------------------
// --- Land names / landmarks (All Other Information) -----------------------
  {
    id: 'l-place-names',
    category: 'other',
    group: 'names',
    build: (src, palette, set) => ({
      id: `${src}-place-names`,
      type: 'symbol',
      source: src,
      filter: [
        'all',
        ['has', 'name', ['get', 'attrs']],
        ['match', ['get', 'objClass'], ['SEAARE', 'LNDMRK', 'BUAARE', 'HRBFAC'], true, false],
      ],
      minzoom: 9,
      layout: {
        'text-field': ['get', 'name', ['get', 'attrs']],
        'text-font': ['Open Sans Regular'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 9, 10, 14, 13],
        'text-optional': true,
      },
      paint: {
        'text-color': labelColourFor(palette, set),
        'text-halo-color': getPalette(palette).DEPDW,
        'text-halo-width': 1.2,
      },
    }),
  },

  // --- Fairways & traffic routes -------------------------------------------
  // A fairway is the charted channel area. Some carry a TRAFIC attribute (the
  // direction traffic runs) and an ORIENT (the lane's bearing), which is how the
  // cells hold a traffic route. We read like a lane, not a faint outline: a soft
  // magenta wash for the area, the dashed magenta edge, and a flow arrow spun to
  // the lane's ORIENT (two heads where it's a two-way lane). These are large-area
  // navigational features, so they come in at low zoom rather than waiting for 11.
  {
    id: 'l-fairway-fill',
    category: 'other',
    group: 'harbour',
    build: (src, palette) => ({
      id: `${src}-fairway-fill`,
      type: 'fill',
      source: src,
      filter: ['all', objEq('FAIRWY'), ['==', ['geometry-type'], 'Polygon']],
      minzoom: 7,
      paint: {
        'fill-color': getPalette(palette).CHMGD,
        'fill-opacity': 0.07,
      },
    }),
  },
  {
    id: 'l-fairway',
    category: 'other',
    group: 'harbour',
    build: (src, palette, set) => ({
      id: `${src}-fairway`,
      type: 'line',
      source: src,
      filter: ['all', objEq('FAIRWY'), ['==', ['geometry-type'], 'Polygon']],
      minzoom: 7,
      paint: {
        'line-color': getPalette(palette).CHMGD,
        'line-width': ['interpolate', ['linear'], ['zoom'], 7, 0.8, 12, 1.4],
        'line-dasharray': magentaDash(set),
        'line-opacity': 0.6,
      },
    }),
  },
  {
    // The flow arrow, only on fairways that carry a TRAFIC attribute. Placed at the
    // lane's centroid and rotated to ORIENT, aligned to the map so it keeps
    // pointing the right way in head-up. Two-way lanes (TRAFIC = 4) take the
    // double-headed arrow, one-way the single head.
    id: 'l-fairway-arrow',
    category: 'other',
    group: 'harbour',
    build: (src) => ({
      id: `${src}-fairway-arrow`,
      type: 'symbol',
      source: src,
      filter: [
        'all',
        objEq('FAIRWY'),
        ['==', ['geometry-type'], 'Polygon'],
        ['!=', ['to-number', ['coalesce', ['get', 'TRAFIC', ['get', 'raw', ['get', 'attrs']]], 0]], 0],
      ],
      minzoom: 8,
      layout: {
        'icon-image': [
          'case',
          ['==', ['to-number', ['coalesce', ['get', 'TRAFIC', ['get', 'raw', ['get', 'attrs']]], 0]], 4],
          'chart-traffic-arrow-two',
          'chart-traffic-arrow',
        ],
        'icon-rotate': ['to-number', ['coalesce', ['get', 'ORIENT', ['get', 'raw', ['get', 'attrs']]], 0]],
        'icon-rotation-alignment': 'map',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 8, 0.45, 11, 0.7, 14, 0.95],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    }),
  },

  // --- Anchorages: the magenta anchor symbol --------------------------------
  {
    id: 'l-anchorage',
    category: 'standard',
    group: 'anchorages',
    build: (src) => ({
      id: `${src}-anchorage`,
      type: 'symbol',
      source: src,
      filter: objEq('ACHARE'),
      minzoom: 11,
      layout: {
        'icon-image': 'chart-anchorage',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 15, 0.85],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    }),
  },

  // --- Moorings: a small magenta dot -----------------------------------------
  {
    id: 'l-mooring',
    category: 'other',
    group: 'anchorages',
    build: (src, palette, set) => ({
      id: `${src}-mooring`,
      type: 'circle',
      source: src,
      filter: ['all', objEq('MORFAC'), ['==', ['geometry-type'], 'Point']],
      minzoom: 13,
      paint: {
        // Paper (INT1): a small open magenta ring. Current/ECDIS: a filled dot.
        'circle-radius': 2.8,
        'circle-color': set === 'np5011' ? 'rgba(0,0,0,0)' : getPalette(palette).CHMGD,
        'circle-stroke-color': getPalette(palette).CHMGD,
        'circle-stroke-width': set === 'np5011' ? 1.4 : 0.6,
      },
    }),
  },

  // --- Traffic separation schemes (Display Standard) --------------------------
  // Carried by the ingest since v2 but never drawn, which for an RYA training
  // app was a hole in the syllabus: the crossing rules the course teaches are
  // ABOUT these shapes. Magenta throughout, as INT1 and S-52 draw routeing
  // measures. Fills are kept faint because they sit in the layer stack above
  // this cell's text; the strong marks are the lines and the lane arrows.
  {
    id: 'l-tss-lane',
    category: 'standard',
    group: 'traffic',
    build: (src, palette) => ({
      id: `${src}-tss-lane`,
      type: 'fill',
      source: src,
      filter: ['all', objIn('TSSLPT', 'TSSRON', 'TSSCRS', 'PRCARE', 'ISTZNE', 'DWRTPT'), ['==', ['geometry-type'], 'Polygon']],
      minzoom: 7,
      paint: {
        'fill-color': getPalette(palette).CHMGD,
        'fill-opacity': 0.07,
      },
    }),
  },
  {
    // One arrow per lane polygon, rotated to the lane's ORIENT attribute, which
    // is the direction of traffic flow in degrees true. The plot-arrow icon
    // points north at rotate 0, so ORIENT rotates it directly; map alignment
    // keeps it true when the chart is head-up. This is the mark a mariner
    // actually reads: the fill says "scheme", the arrow says "which way".
    id: 'l-tss-lane-arrow',
    category: 'standard',
    group: 'traffic',
    build: (src) => ({
      id: `${src}-tss-lane-arrow`,
      type: 'symbol',
      source: src,
      filter: ['all', objEq('TSSLPT'), ['==', ['geometry-type'], 'Polygon']],
      minzoom: 8,
      layout: {
        'icon-image': 'plot-arrow',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 8, 0.8, 13, 1.4],
        'icon-rotate': ['coalesce', ['get', 'ORIENT', ['get', 'raw', ['get', 'attrs']]], 0],
        'icon-rotation-alignment': 'map',
        'icon-allow-overlap': true,
      },
      paint: { 'icon-opacity': 0.55 },
    }),
  },
  {
    id: 'l-tss-separation',
    category: 'standard',
    group: 'traffic',
    build: (src, palette) => ({
      id: `${src}-tss-separation`,
      type: 'fill',
      source: src,
      filter: ['all', objEq('TSEZNE'), ['==', ['geometry-type'], 'Polygon']],
      minzoom: 7,
      paint: {
        'fill-color': getPalette(palette).CHMGD,
        'fill-opacity': 0.18,
      },
    }),
  },
  {
    id: 'l-tss-sepline',
    category: 'standard',
    group: 'traffic',
    build: (src, palette) => ({
      id: `${src}-tss-sepline`,
      type: 'line',
      source: src,
      filter: objEq('TSELNE'),
      minzoom: 7,
      paint: {
        'line-color': getPalette(palette).CHMGD,
        'line-width': 2.5,
        'line-opacity': 0.6,
      },
    }),
  },
  {
    id: 'l-tss-boundary',
    category: 'standard',
    group: 'traffic',
    build: (src, palette) => ({
      id: `${src}-tss-boundary`,
      type: 'line',
      source: src,
      filter: objIn('TSSBND', 'DWRTCL', 'RCRTCL', 'FERYRT'),
      minzoom: 7,
      paint: {
        'line-color': getPalette(palette).CHMGD,
        'line-width': 1.8,
        'line-dasharray': [3, 2],
        'line-opacity': 0.8,
      },
    }),
  },

  // --- Restricted / caution areas (Display Standard) --------------------------
  // RESARE is where the "why" lives on a real chart: firing ranges, nature
  // reserves, no-anchoring. The fill is deliberately near-invisible; the T-dash
  // style edge is the mark. The reason text is in attrs and shows on tap.
  {
    id: 'l-restricted-fill',
    category: 'standard',
    group: 'restricted-areas',
    build: (src, palette) => ({
      id: `${src}-restricted-fill`,
      type: 'fill',
      source: src,
      filter: ['all', objIn('RESARE', 'MIPARE', 'MPAARE', 'CTNARE', 'TESARE', 'DMPGRD', 'SPLARE'), ['==', ['geometry-type'], 'Polygon']],
      minzoom: 8,
      paint: {
        'fill-color': getPalette(palette).CHMGD,
        'fill-opacity': 0.05,
      },
    }),
  },
  {
    id: 'l-restricted-edge',
    category: 'standard',
    group: 'restricted-areas',
    build: (src, palette) => ({
      id: `${src}-restricted-edge`,
      type: 'line',
      source: src,
      filter: objIn('RESARE', 'MIPARE', 'MPAARE', 'CTNARE', 'TESARE', 'DMPGRD', 'SPLARE'),
      minzoom: 8,
      paint: {
        'line-color': getPalette(palette).CHMGD,
        'line-width': 1.4,
        // INT1 draws these as a T-dash; the closest a dasharray gets is
        // dash-gap-dot-gap, which reads as "special boundary" at chart size.
        'line-dasharray': [4, 2, 1, 2],
        'line-opacity': 0.8,
      },
    }),
  },

  // --- Submarine cables and pipelines (own toggle, default off) ---------------
  // S-52 files these under Display Other. Anchoring on one is the practical
  // hazard, so they earn a place, but they clutter a harbour fast, so the group
  // ships OFF and the store default keeps it that way until switched on.
  {
    id: 'l-cable-line',
    category: 'standard',
    group: 'cables',
    build: (src, palette) => ({
      id: `${src}-cable-line`,
      type: 'line',
      source: src,
      filter: objIn('CBLSUB', 'CBLOHD'),
      minzoom: 10,
      paint: {
        'line-color': getPalette(palette).CHMGD,
        'line-width': 1.1,
        'line-dasharray': [6, 2],
        'line-opacity': 0.8,
      },
    }),
  },
  {
    id: 'l-pipeline-line',
    category: 'standard',
    group: 'cables',
    build: (src, palette) => ({
      id: `${src}-pipeline-line`,
      type: 'line',
      source: src,
      filter: objIn('PIPSOL', 'PIPOHD'),
      minzoom: 10,
      paint: {
        'line-color': getPalette(palette).CHMGD,
        'line-width': 1.1,
        'line-dasharray': [2, 2],
        'line-opacity': 0.8,
      },
    }),
  },
  {
    id: 'l-cable-area',
    category: 'standard',
    group: 'cables',
    build: (src, palette) => ({
      id: `${src}-cable-area`,
      type: 'line',
      source: src,
      filter: ['all', objIn('CBLARE', 'PIPARE'), ['==', ['geometry-type'], 'Polygon']],
      minzoom: 10,
      paint: {
        'line-color': getPalette(palette).CHMGD,
        'line-width': 1,
        'line-dasharray': [6, 2],
        'line-opacity': 0.7,
      },
    }),
  },

  // --- Anchorage area boundary + anchor berths --------------------------------
  // The anchorage SYMBOL was drawn already; the area's extent was not, so a
  // designated anchorage read as one anchor floating in blank water and its
  // edge, the thing that decides whether we are inside it, was invisible.
  {
    id: 'l-anchorage-edge',
    category: 'standard',
    group: 'anchorages',
    build: (src, palette) => ({
      id: `${src}-anchorage-edge`,
      type: 'line',
      source: src,
      filter: ['all', objEq('ACHARE'), ['==', ['geometry-type'], 'Polygon']],
      minzoom: 10,
      paint: {
        'line-color': getPalette(palette).CHMGD,
        'line-width': 1.2,
        'line-dasharray': [5, 2],
        'line-opacity': 0.7,
      },
    }),
  },
  {
    id: 'l-anchor-berth',
    category: 'other',
    group: 'anchorages',
    build: (src) => ({
      id: `${src}-anchor-berth`,
      type: 'symbol',
      source: src,
      filter: objEq('ACHBRT'),
      minzoom: 12,
      layout: {
        'icon-image': 'chart-anchorage',
        'icon-size': 0.45,
        'icon-allow-overlap': true,
      },
    }),
  },

  // --- Landmarks other than lighthouses: a small open circle (named on tap) -
  {
    id: 'l-landmark',
    category: 'standard',
    group: 'landmarks',
    build: (src, palette, set) => ({
      id: `${src}-landmark`,
      type: 'circle',
      source: src,
      filter: [
        'all',
        objEq('LNDMRK'),
        ['==', ['geometry-type'], 'Point'],
        ['!', ['in', '17', ['coalesce', ['get', 'CATLMK', ['get', 'raw', ['get', 'attrs']]], ['literal', []]]]],
      ],
      minzoom: 12,
      paint: {
        // Open circle (position of a landmark). Paper draws it a touch heavier.
        'circle-radius': set === 'np5011' ? 3.2 : 3,
        'circle-color': 'rgba(0,0,0,0)',
        'circle-stroke-color': getPalette(palette).CHBLK,
        'circle-stroke-width': set === 'np5011' ? 1.6 : set === 'np5012' ? 1 : 1.2,
      },
    }),
  },
];

/** Reserved for later — per-feature SCAMIN filtering hook. */
export function applyScamin(layer: LayerSpecification): LayerSpecification {
  return layer;
}
